import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchClergyBySlug } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Mail, Phone, Calendar, Heart, GraduationCap, 
  MapPin, Church, ArrowLeft, Quote, Shield, ExternalLink
} from 'lucide-react';
import { motion } from 'framer-motion';
import SeoHead from '@/components/Seo';
import ImageHelper from '../lib/imageHelper';

const ClergyDetail = () => {
  const { slug } = useParams();
  const [clergy, setClergy] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const loadClergy = async () => {
      try {
        const response = await fetchClergyBySlug(slug);
        setClergy(response.data);
      } catch (error) {
        toast({ title: 'Error', description: 'Failed to fetch clergy details.', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };
    loadClergy();
  }, [slug, toast]);

  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#fcfaf7]">
      <div className="h-12 w-12 border-4 border-amber-600/10 border-t-amber-600 rounded-full animate-spin mb-4" />
      <p className="text-slate-400 font-serif italic animate-pulse">Consulting the registry...</p>
    </div>
  );

  if (!clergy) return <div className="p-20 text-center text-slate-500 font-serif">Clergy record not found.</div>;

  return (
    <div className="min-h-screen bg-[#fcfaf7] pb-32">
      <SeoHead
        title={`${clergy.title} ${clergy.name} | Ibadan South Diocese`}
        description={clergy.biography?.substring(0, 160) || "Clergy profile details."}
        ogImage={clergy.photo}
      />

      {/* Cinematic Hero Header */}
      <div className="h-[40vh] bg-slate-950 relative overflow-hidden">
        <motion.img 
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.4 }}
          transition={{ duration: 1.5 }}
          src="https://res.cloudinary.com/dwrqyrm84/image/upload/v1750874183/home_ss1tdy.jpg" 
          className="w-full h-full object-cover grayscale"
          alt="texture"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#fcfaf7] via-[#fcfaf7]/20 to-transparent" />
      </div>

      <div className="max-w-6xl mx-auto px-6 -mt-48 relative z-20">
        <Link to="/clergy" className="inline-flex items-center text-white/90 hover:text-white mb-8 group bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20 transition-all">
          <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
          <span className="text-xs font-black uppercase tracking-widest">Directory</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Sidebar: Profile Card */}
          <div className="lg:col-span-4 sticky top-8">
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="bg-white rounded-[3rem] shadow-2xl shadow-slate-200/50 p-4 border border-slate-100"
            >
              <div className="aspect-[4/5] rounded-[2.5rem] overflow-hidden bg-slate-100 mb-8 group relative">
                <ImageHelper 
                   src={clergy.photo} 
                   alt={clergy.name} 
                   className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                />
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-3 rounded-2xl">
                   <Shield className="h-5 w-5 text-amber-600" />
                </div>
              </div>

              <div className="px-6 pb-8 text-center">
                <p className="text-amber-600 font-black text-[10px] uppercase tracking-[0.3em] mb-3">
                  {clergy.title}
                </p>
                <h1 className="text-3xl font-serif font-bold text-slate-900 leading-tight mb-8">
                  {clergy.name}
                </h1>
                
                <div className="space-y-3">
                  {clergy.email && (
                    <a href={`mailto:${clergy.email}`} className="flex items-center justify-center gap-3 p-4 bg-slate-50 rounded-2xl text-slate-600 hover:bg-amber-600 hover:text-white transition-all group">
                      <Mail className="h-4 w-4 opacity-50 group-hover:opacity-100" />
                      <span className="text-xs font-bold truncate tracking-tight">{clergy.email}</span>
                    </a>
                  )}
                  {clergy.phone && (
                    <a href={`tel:${clergy.phone}`} className="flex items-center justify-center gap-3 p-4 bg-slate-50 rounded-2xl text-slate-600 hover:bg-emerald-600 hover:text-white transition-all group">
                      <Phone className="h-4 w-4 opacity-50 group-hover:opacity-100" />
                      <span className="text-xs font-bold tracking-tight">{clergy.phone}</span>
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-8 space-y-10">
            
            {/* Stats Bar */}
            <motion.div 
               initial={{ opacity: 0, x: 20 }}
               animate={{ opacity: 1, x: 0 }}
               className="grid grid-cols-2 md:grid-cols-4 gap-4"
            >
               {[
                 { label: 'Born', val: clergy.dob, icon: Calendar },
                 { label: 'Married', val: clergy.wedding, icon: Heart },
                 { label: 'Leisure', val: clergy.hobby, icon: GraduationCap },
                 { label: 'Passion', val: clergy.passion, icon: Quote }
               ].map((stat, i) => stat.val && (
                 <div key={i} className="bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col items-center text-center">
                   <div className="p-2 bg-amber-50 rounded-xl mb-3">
                      <stat.icon className="h-4 w-4 text-amber-600" />
                   </div>
                   <p className="text-[10px] uppercase font-black text-slate-300 tracking-widest mb-1">{stat.label}</p>
                   <p className="text-slate-900 font-bold text-xs truncate w-full">{stat.val}</p>
                 </div>
               ))}
            </motion.div>

            {/* Assignments Panel */}
            {clergy.parishes && clergy.parishes.length > 0 && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-slate-900 rounded-[3rem] p-10 text-white relative overflow-hidden group shadow-2xl shadow-slate-900/20"
              >
                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
                   <Church size={120} />
                </div>
                
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="h-px w-8 bg-amber-500" />
                    <p className="text-[10px] font-black uppercase tracking-[0.4em] text-amber-500">Current Stationing</p>
                  </div>
                  
                  <div className="space-y-8">
                    {clergy.parishes.map((p, idx) => (
                      <div key={p.id} className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="space-y-2">
                          <Link to={`/parishes/${p.slug}`} className="group/link flex items-center gap-2">
                            <h3 className="text-3xl font-serif font-bold group-hover/link:text-amber-400 transition-colors">{p.name}</h3>
                            <ExternalLink size={16} className="opacity-0 group-hover/link:opacity-100 text-amber-400 transition-all" />
                          </Link>
                          {p.archdeaconry && (
                            <p className="text-xs font-medium text-slate-400 uppercase tracking-widest">{p.archdeaconry.name} Archdeaconry</p>
                          )}
                        </div>
                        <div className="flex items-center gap-3 bg-white/5 border border-white/10 px-5 py-3 rounded-2xl self-start">
                          <MapPin className="h-4 w-4 text-amber-500" />
                          <span className="text-sm font-medium">{p.location || 'Diocesan Territory'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Biography Section */}
            <Card className="border-none shadow-sm rounded-[3rem] overflow-hidden bg-white">
              <CardContent className="p-10 md:p-16">
                <div className="flex items-center gap-6 mb-12">
                  <h2 className="text-3xl font-serif font-bold text-slate-900">Life & Ministry</h2>
                  <div className="h-px flex-1 bg-slate-100" />
                </div>

                {clergy.qualification && (
                  <div className="mb-12 flex flex-col md:flex-row gap-6 items-start p-8 bg-amber-50 rounded-[2.5rem] border border-amber-100/50">
                    <div className="bg-white p-4 rounded-2xl shadow-sm shadow-amber-200/50">
                        <GraduationCap className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <span className="font-black uppercase text-[10px] text-amber-700/60 block tracking-widest mb-2">Ecclesiastical & Academic Credentials</span>
                      <p className="text-slate-800 font-serif text-xl italic leading-relaxed">
                        {clergy.qualification}
                      </p>
                    </div>
                  </div>
                )}

                <div className="prose prose-slate max-w-none prose-lg">
                  {clergy.biography ? (
                    <div
                      className="whitespace-pre-wrap text-slate-700 leading-[1.8] text-xl font-serif first-letter:text-5xl first-letter:font-bold first-letter:text-amber-600 first-letter:mr-3 first-letter:float-left"
                      dangerouslySetInnerHTML={{ __html: clergy.biography.replace(/\r?\n/g, '<br/>') }}
                    />
                  ) : (
                    <div className="py-20 text-center bg-slate-50 rounded-[2.5rem] border border-dashed border-slate-200">
                        <Quote className="h-12 w-12 text-slate-100 mx-auto mb-6" />
                        <p className="text-slate-400 italic font-serif text-lg max-w-md mx-auto px-6">
                          "The journey of faith is a story still being written. This biography is currently being curated by the Diocesan Office."
                        </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ClergyDetail;